import { Location } from '@angular/common';
import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

import {
  CardCheckoutConfigComponent,
  CheckoutAppearanceConfigComponent,
  CountryAccessConfigComponent,
  PaymentOptionsConfigComponent,
  type CardCheckoutSettings,
  type CheckoutAppearanceValue,
  type CountryAccessValue,
  type CountryOption,
  type PaymentOptionsValue,
} from '../../components/merchant-config';
import { UI, createServerSearch } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';
import { HubToastService } from '../hub/ui/hub-toast.service';
import {
  FIELD_STEP,
  STEPS,
  buildPayload,
  fieldErrors,
  initialState,
  validateStep,
  type MerchantWizardState,
  type StepId,
} from './merchant-create.model';

/** Wizard for `/manage/merchants/new`: replaces the generic dynamic form with structured editors. */
@Component({
  selector: 'app-merchant-create',
  standalone: true,
  imports: [
    FormsModule,
    CardCheckoutConfigComponent,
    CheckoutAppearanceConfigComponent,
    CountryAccessConfigComponent,
    PaymentOptionsConfigComponent,
    ...UI,
  ],
  template: `
    <div class="mx-auto max-w-4xl">
      <div class="mb-4 flex items-center justify-between gap-3">
        <h1 class="m-0 text-xl font-bold text-fg">Add merchant</h1>
        <button type="button" uiButton variant="ghost" size="sm" (click)="cancel()">Cancel</button>
      </div>

      <ol class="mb-5 flex flex-wrap gap-2 p-0 list-none" aria-label="Steps">
        @for (s of steps; track s.id; let i = $index) {
          <li>
            <button
              type="button"
              class="flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors"
              [class]="
                step() === s.id
                  ? 'border-primary bg-primary/10 text-fg'
                  : stepHasError(s.id)
                    ? 'border-danger text-danger'
                    : 'border-border text-muted hover:text-fg'
              "
              [attr.aria-current]="step() === s.id ? 'step' : null"
              (click)="goTo(s.id)"
            >
              <span>{{ i + 1 }}</span> {{ s.label }}
            </button>
          </li>
        }
      </ol>

      <section class="rounded-xl border border-border bg-card px-5 py-4">
        @switch (step()) {
          @case ('business') {
            <h2 class="m-0 mb-3 text-base font-bold text-fg">Business details</h2>
            <div class="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-x-4">
              <ui-form-field label="Business name" [required]="true" [error]="err('name')">
                <input uiInput [ngModel]="state().business.name" (ngModelChange)="setBusiness('name', $event)" />
              </ui-form-field>
              <ui-form-field label="Legal name" [error]="err('legal_name')">
                <input uiInput [ngModel]="state().business.legal_name" (ngModelChange)="setBusiness('legal_name', $event)" />
              </ui-form-field>
              <ui-form-field label="Contact email" [required]="true" [error]="err('email')">
                <input uiInput type="email" [ngModel]="state().business.email" (ngModelChange)="setBusiness('email', $event)" />
              </ui-form-field>
              <ui-form-field label="Notification email" hint="Receives monthly statistics and other mailings. Falls back to the contact email." [error]="err('notification_email')">
                <input uiInput type="email" [ngModel]="state().business.notification_email" (ngModelChange)="setBusiness('notification_email', $event)" />
              </ui-form-field>
              <ui-form-field label="Phone number" [error]="err('phone_number')">
                <input uiInput maxlength="20" [ngModel]="state().business.phone_number" (ngModelChange)="setBusiness('phone_number', $event)" />
              </ui-form-field>
              <ui-form-field label="Country" [required]="true" [error]="err('country')">
                <ui-autocomplete
                  placeholder="Select a country…" ariaLabel="Country"
                  [options]="countrySearch.options()" [loading]="countrySearch.loading()" [filterLocal]="false"
                  [ngModel]="state().business.country" (ngModelChange)="setCountry($event)"
                  (search)="countrySearch.search($event)"
                />
              </ui-form-field>
              <ui-form-field label="Registration number" [error]="err('registration_number')">
                <input uiInput [ngModel]="state().business.registration_number" (ngModelChange)="setBusiness('registration_number', $event)" />
              </ui-form-field>
              <ui-form-field label="Tax ID" [error]="err('tax_id')">
                <input uiInput [ngModel]="state().business.tax_id" (ngModelChange)="setBusiness('tax_id', $event)" />
              </ui-form-field>
              <ui-form-field label="Logo" hint="PNG or JPEG image." [error]="err('logo')">
                <input type="file" accept="image/*" class="text-sm" (change)="onLogo($event)" />
              </ui-form-field>
            </div>
            <ui-switch [checked]="state().business.is_active" (changed)="setBusiness('is_active', $event)">
              Active<span description>Inactive merchants cannot transact.</span>
            </ui-switch>
          }
          @case ('checkout') {
            <h2 class="m-0 mb-3 text-base font-bold text-fg">Checkout &amp; payments</h2>
            @for (f of ['checkout_primary_color','checkout_secondary_color','checkout_accent_color','checkout_background_color']; track f) {
              @if (err(f)) { <p class="my-1 text-[0.8125rem] text-danger">{{ err(f) }}</p> }
            }
            <mm-checkout-appearance-config [value]="state().appearance" (valueChange)="patch('appearance', $event)" />
            @if (err('checkout_custom_css')) { <p class="my-1 text-[0.8125rem] text-danger">{{ err('checkout_custom_css') }}</p> }
            <hr class="my-4 border-border" />
            <mm-payment-options-config [value]="state().payments" (valueChange)="patch('payments', $event)" />
          }
          @case ('card') {
            <h2 class="m-0 mb-3 text-base font-bold text-fg">Card checkout</h2>
            @if (!state().payments.checkout_card_enabled) {
              <ui-alert variant="info">
                Card payments are switched off in the previous step. You can still prepare these settings.
              </ui-alert>
            }
            <mm-card-checkout-config [value]="state().card" (valueChange)="patch('card', $event)" />
            @if (err('card_checkout_settings')) { <p class="my-1 text-[0.8125rem] text-danger">{{ err('card_checkout_settings') }}</p> }
          }
          @case ('countries') {
            <h2 class="m-0 mb-3 text-base font-bold text-fg">Countries</h2>
            <mm-country-access-config [(countries)]="countries" [value]="state().countries" (valueChange)="patch('countries', $event)" />
            @if (err('allowed_countries') || err('disallowed_countries') || err('non_field_errors')) {
              <p class="my-1 text-[0.8125rem] text-danger">{{ err('allowed_countries') }} {{ err('disallowed_countries') }} {{ err('non_field_errors') }}</p>
            }
          }
          @case ('integration') {
            <h2 class="m-0 mb-3 text-base font-bold text-fg">Integration</h2>
            <div class="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-x-4">
              <ui-form-field label="Webhook URL" hint="HTTPS endpoint that receives events." [error]="err('webhook_url')">
                <input uiInput type="url" [ngModel]="state().integration.webhook_url" (ngModelChange)="setIntegration('webhook_url', $event)" />
              </ui-form-field>
              <ui-form-field label="Webhook secret" hint="Stored hashed; shown to nobody afterwards." [error]="err('webhook_secret')">
                <input uiInput type="password" autocomplete="new-password" [ngModel]="state().integration.webhook_secret" (ngModelChange)="setIntegration('webhook_secret', $event)" />
              </ui-form-field>
              <ui-form-field label="Email sender name" [error]="err('email_from_name')">
                <input uiInput maxlength="64" [ngModel]="state().integration.email_from_name" (ngModelChange)="setIntegration('email_from_name', $event)" />
              </ui-form-field>
            </div>
            <ui-switch [checked]="state().integration.developer_can_view_transactions" (changed)="setIntegration('developer_can_view_transactions', $event)">
              Developers can view transactions
            </ui-switch>
            <h3 class="mx-0 mb-2 mt-5 text-sm font-bold text-fg">Metadata</h3>
            @for (row of state().integration.metadata; track $index; let i = $index) {
              <div class="mb-2 flex gap-2">
                <input uiInput placeholder="key" aria-label="Metadata key" [ngModel]="row.key" (ngModelChange)="setMeta(i, 'key', $event)" />
                <input uiInput placeholder="value" aria-label="Metadata value" [ngModel]="row.value" (ngModelChange)="setMeta(i, 'value', $event)" />
                <button type="button" uiButton variant="ghost" size="sm" (click)="removeMeta(i)">Remove</button>
              </div>
            }
            @if (err('metadata')) { <p class="my-1 text-[0.8125rem] text-danger">{{ err('metadata') }}</p> }
            <button type="button" uiButton variant="secondary" size="sm" (click)="addMeta()">Add entry</button>
          }
          @case ('review') {
            <h2 class="m-0 mb-3 text-base font-bold text-fg">Review</h2>
            @for (group of summary(); track group.step) {
              <div class="mb-3 rounded-lg border border-border px-4 py-3">
                <div class="mb-1 flex items-center justify-between">
                  <h3 class="m-0 text-sm font-bold text-fg">{{ group.title }}</h3>
                  <button type="button" uiButton variant="ghost" size="sm" (click)="goTo(group.step)">Edit</button>
                </div>
                <dl class="m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 text-[0.8125rem]">
                  @for (row of group.rows; track row[0]) {
                    <dt class="text-muted">{{ row[0] }}</dt><dd class="m-0 break-words text-fg">{{ row[1] }}</dd>
                  }
                </dl>
              </div>
            }
            @if (submitError()) { <ui-alert variant="danger">{{ submitError() }}</ui-alert> }
          }
        }
      </section>

      <div class="mt-4 flex justify-between">
        <button type="button" uiButton variant="secondary" [disabled]="index() === 0" (click)="back()">Back</button>
        @if (step() === 'review') {
          <button type="button" uiButton variant="primary" [disabled]="submitting()" (click)="submit()">
            {{ submitting() ? 'Creating…' : 'Create merchant' }}
          </button>
        } @else {
          <button type="button" uiButton variant="primary" (click)="next()">Next</button>
        }
      </div>
    </div>
  `,
})
export class MerchantCreateComponent implements OnInit {
  @Input() entityConfig?: EntityConfig;

  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly shellRoutes = inject(ShellRoutesService);
  private readonly toast = inject(HubToastService);

  readonly steps = STEPS;
  readonly step = signal<StepId>('business');
  readonly state = signal<MerchantWizardState>(initialState());
  /** Countries seen so far (business country search and the allow/deny lists), to label ids in the review. */
  readonly countries = signal<readonly CountryOption[]>([]);
  readonly countrySearch = createServerSearch<CountryOption>({
    fetch: (term) =>
      this.api
        .get<{ results?: Array<{ id: string | number; code?: string; name: string }> }>('finances/countries/', {
          page: 1,
          search: term,
          is_active: 'true',
        })
        .pipe(
          map((res) =>
            (res.results ?? []).map((r) => ({ id: String(r.id), code: r.code ?? '', name: r.name })),
          ),
          catchError(() => {
            this.toast.error('Could not load countries.');
            return of([] as CountryOption[]);
          }),
        ),
    toOption: (row) => ({ value: row.id, label: row.name }),
    selected: () => (this.state().business.country ? [this.state().business.country] : []),
  });
  readonly errors = signal<Record<string, string>>({});
  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);
  readonly index = computed(() => STEPS.findIndex((s) => s.id === this.step()));

  readonly summary = computed(() => {
    const s = this.state();
    const country = this.countries().find((c) => c.id === s.business.country)?.name ?? '—';
    const names = (ids: string[]): string =>
      ids.map((id) => this.countries().find((c) => c.id === id)?.name ?? id).join(', ') || 'none';
    const yn = (v: boolean): string => (v ? 'Yes' : 'No');
    return [
      { step: 'business' as StepId, title: 'Business', rows: [
        ['Name', s.business.name], ['Email', s.business.email], ['Notification email', s.business.notification_email || '—'], ['Country', country],
        ['Active', yn(s.business.is_active)], ['Logo', s.business.logo?.name ?? '—'],
      ] },
      { step: 'checkout' as StepId, title: 'Checkout & payments', rows: [
        ['Primary colour', s.appearance.checkout_primary_color], ['Language', s.appearance.checkout_default_locale],
        ['Fees paid by', s.payments.fee_bearer], ['Card in checkout', yn(s.payments.checkout_card_enabled)],
        ['MainMoney in checkout', yn(s.payments.checkout_mainmoney_enabled)],
        ['Disabled link methods', s.payments.payment_link_disabled_methods.join(', ') || 'none'],
      ] },
      { step: 'card' as StepId, title: 'Card checkout', rows: [
        ['Payment types', (s.card.payment_types ?? []).join(', ') || 'provider default'],
        ['Card networks', (s.card.card_networks ?? []).join(', ') || 'provider default'],
        ['Customised appearance', String(Object.keys(s.card.appearance_overrides ?? {}).length) + ' overrides'],
      ] },
      { step: 'countries' as StepId, title: 'Countries', rows: [
        ['Allowed', names(s.countries.allowed)], ['Disallowed', names(s.countries.disallowed)],
      ] },
      { step: 'integration' as StepId, title: 'Integration', rows: [
        ['Webhook URL', s.integration.webhook_url || '—'],
        ['Webhook secret', s.integration.webhook_secret ? 'set' : '—'],
        ['Metadata entries', String(s.integration.metadata.filter((r) => r.key.trim()).length)],
      ] },
    ] satisfies Array<{ step: StepId; title: string; rows: string[][] }>;
  });

  ngOnInit(): void {
    this.countrySearch.search('');
  }

  setCountry(id: string | null): void {
    const value = id ?? '';
    const picked = this.countrySearch.options().find((o) => o.value === value);
    if (picked && !this.countries().some((c) => c.id === value)) {
      this.countries.update((list) => [...list, { id: value, code: '', name: picked.label }]);
    }
    this.setBusiness('country', value);
  }

  err(field: string): string {
    return this.errors()[field] ?? '';
  }

  stepHasError(id: StepId): boolean {
    return Object.keys(this.errors()).some((f) => (FIELD_STEP[f] ?? 'review') === id);
  }

  patch<K extends 'appearance' | 'payments' | 'card' | 'countries'>(
    key: K,
    value: CheckoutAppearanceValue | PaymentOptionsValue | CardCheckoutSettings | CountryAccessValue,
  ): void {
    this.state.update((s) => ({ ...s, [key]: value }));
  }

  setBusiness<K extends keyof MerchantWizardState['business']>(key: K, value: MerchantWizardState['business'][K]): void {
    this.state.update((s) => ({ ...s, business: { ...s.business, [key]: value } }));
  }

  setIntegration<K extends keyof MerchantWizardState['integration']>(key: K, value: MerchantWizardState['integration'][K]): void {
    this.state.update((s) => ({ ...s, integration: { ...s.integration, [key]: value } }));
  }

  addMeta(): void {
    this.setIntegration('metadata', [...this.state().integration.metadata, { key: '', value: '' }]);
  }

  removeMeta(i: number): void {
    this.setIntegration('metadata', this.state().integration.metadata.filter((_, idx) => idx !== i));
  }

  setMeta(i: number, field: 'key' | 'value', text: string): void {
    this.setIntegration(
      'metadata',
      this.state().integration.metadata.map((row, idx) => (idx === i ? { ...row, [field]: text } : row)),
    );
  }

  onLogo(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.setBusiness('logo', file);
  }

  next(): void {
    const errors = validateStep(this.step(), this.state());
    this.errors.set(errors);
    if (Object.keys(errors).length) return;
    this.step.set(STEPS[Math.min(this.index() + 1, STEPS.length - 1)].id);
  }

  back(): void {
    this.errors.set({});
    this.step.set(STEPS[Math.max(this.index() - 1, 0)].id);
  }

  /** Jump freely backwards; forwards only when every step in between validates. */
  goTo(target: StepId): void {
    const to = STEPS.findIndex((s) => s.id === target);
    for (let i = this.index(); i < to; i++) {
      const errors = validateStep(STEPS[i].id, this.state());
      if (Object.keys(errors).length) {
        this.errors.set(errors);
        this.step.set(STEPS[i].id);
        return;
      }
    }
    this.errors.set({});
    this.step.set(target);
  }

  submit(): void {
    for (const s of STEPS) {
      const errors = validateStep(s.id, this.state());
      if (Object.keys(errors).length) {
        this.errors.set(errors);
        this.step.set(s.id);
        return;
      }
    }
    this.submitting.set(true);
    this.submitError.set(null);
    this.api.post<Record<string, unknown>>('auth/merchants/', buildPayload(this.state())).subscribe({
      next: (res) => {
        this.submitting.set(false);
        this.toast.success('Merchant created.');
        const id = res['id'] ?? res['pk'];
        void this.router.navigate(
          this.shellRoutes.commands('manage', 'merchants', ...(id != null ? [String(id)] : [])),
        );
      },
      error: (err: { error?: unknown }) => {
        this.submitting.set(false);
        const fields = fieldErrors(err?.error);
        this.errors.set(fields);
        const first = Object.keys(fields)[0];
        if (first) {
          this.step.set(FIELD_STEP[first] ?? 'review');
        }
        // Unknown fields and server-side failures get a fixed, safe message (no raw exception text).
        this.submitError.set(
          first && !(first in FIELD_STEP)
            ? `The server rejected "${first}": ${fields[first]}`
            : 'The merchant could not be created. Review the highlighted steps.',
        );
      },
    });
  }

  cancel(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', 'merchants'));
  }
}
